import { ASCIIDOC, MARKDOWN, bare } from '../markup'

/**
 * Which renderer draws which markup — the lookup a markup language is an entry in.
 *
 * Three things about its shape are deliberate, and the second entry is what they were for:
 *
 * - **it is asynchronous.** Asciidoctor is most of a megabyte and has no business in the bundle
 *   everybody downloads to look at a folder, so it is fetched the first time somebody opens an AsciiDoc
 *   text. A synchronous interface would have had to be torn up for that.
 * - **it says how it is triggered.** Both markups draw themselves as fast as somebody types; a typeset
 *   PDF for handing work in takes seconds and belongs behind a control. A lookup that assumed the first
 *   would make the second a special case outside it.
 * - **it does not have to answer with HTML.** The typesetting issue answers with a PDF. A signature
 *   returning `string` would make that a rewrite rather than an entry.
 *
 * **What is drawn is what the *content* says it is**, never what the editor is. A note carries its format
 * in a column, a document carries it as the media type of its file, and both arrive here as the same
 * string.
 */

/** How a rendering is asked for. */
export type Trigger =
  /** Fast enough to redraw as somebody types. What Markdown in the browser is. */
  | 'as-you-type'
  /** Slow or expensive, so it happens when it is asked for. What a typeset PDF will be. */
  | 'on-request'

/** What came out of a renderer. */
export type Rendering =
  /** Markup to put into the page. Sanitised by the renderer; see the note in `markdown.ts`. */
  | { readonly kind: 'html'; readonly html: string }
  /** A file to show rather than to inline — a typeset PDF. Nothing produces one yet. */
  | { readonly kind: 'file'; readonly url: string; readonly mediaType: string }

export interface Renderer {
  /** How it is triggered, which decides whether a surface redraws on every keystroke. */
  readonly trigger: Trigger
  /** What it makes of a text. Asynchronous so that a heavy renderer can be fetched on first use. */
  render(text: string): Promise<Rendering>
}

/**
 * Loading a renderer is itself asynchronous: the module holding it may not be in the bundle yet.
 *
 * `null` for a format nothing here can draw, which is not a failure — it is a text somebody can still
 * write, and the surface simply offers no preview for it.
 */
export type RendererFor = (format: string) => Promise<Renderer | null>

/**
 * The renderer for a format, or `null`.
 *
 * Both are imported dynamically, so the rule is the same for every entry rather than "the first one is
 * special". For Markdown that costs one microtask on first use and keeps the shape honest; for AsciiDoc
 * it is the point.
 */
export const rendererFor: RendererFor = async (format) => {
  switch (bare(format)) {
    case MARKDOWN:
      return (await import('./markdown')).markdownRenderer
    case ASCIIDOC:
      return (await import('./asciidoc')).asciidocRenderer
    default:
      return null
  }
}
